/**
 * What a character has going for them, as numbers the combat model already takes.
 *
 * A `Loadout` is not only gear, despite living here: it is the shape every producer of character
 * modifiers fills in, and equipment was simply the first one written. Perks fill in the same shape
 * in `sim/progression/perkLoadout.ts`, and `combineLoadouts` adds the two together, which is what
 * keeps there from being a second modifier system for the pipeline to know about.
 *
 * One pass over `SLOT_ORDER`, one result, and no edits anywhere in `sim/combat`. Gear reaches the
 * damage pipeline through the two seams it already has: `DamageModifier` at stage 3 and
 * `ArmorProfile` at stage 7. Stat deltas are the third output, applied to the character's block
 * before any of it runs, and the lever bundle is the fourth.
 *
 * Summing in slot order is part of the determinism contract, because floating point addition is
 * not associative and two peers must agree on the last bit.
 */

import { EMPTY_EFFECTS, combineEffects } from '@/sim/effects/characterEffects';
import { STAT_ORDER, statsFrom } from '@/sim/combat/statContract';
import { SLOT_ORDER } from '@/sim/inventory/slotRegistry';
import { itemDef } from '@/sim/items/itemRegistry';
import type { ArmorProfile } from '@/sim/combat/armorMitigation';
import type { EffectBundle } from '@/sim/effects/characterEffects';
import type { DamageModifier } from '@/sim/combat/damageResolution';
import type { StatBlock, StatId } from '@/sim/combat/statContract';
import type { Equipment } from '@/sim/inventory/inventoryContract';

export type StatDeltas = Partial<Record<StatId, number>>;

export interface Loadout {
  readonly statDeltas: StatDeltas;
  readonly armor: ArmorProfile;
  readonly damageModifiers: readonly DamageModifier[];
  /**
   * The levers, multiplicative, identity one. Gear contributes nothing here yet: widening
   * `EquipEffect` to carry levers is a deliberate edit and reserving the field on every item file
   * before one needs it would be speculative config.
   */
  readonly effects: EffectBundle;
}

/** A character wearing nothing. The identity of every sum below. */
export const EMPTY_LOADOUT: Loadout = {
  statDeltas: {},
  armor: { threshold: 0, rating: 0 },
  damageModifiers: [],
  effects: EMPTY_EFFECTS,
};

/** Everything equipped, added up. The one function gear reaches the combat model through. */
export function aggregateLoadout(equipment: Equipment): Loadout {
  const statDeltas: StatDeltas = {};
  const damageModifiers: DamageModifier[] = [];
  let threshold = 0;
  let rating = 0;

  for (const slot of SLOT_ORDER) {
    const stack = equipment[slot];
    if (stack === null) continue;

    const effect = itemDef(stack.defId).equip;
    if (effect === null) continue;

    threshold += effect.armor.threshold;
    rating += effect.armor.rating;
    damageModifiers.push(...effect.damageModifiers);

    // Walked in stat order rather than by object key, so the sum is the same on every machine
    // and nothing has to be cast back out of an entry pair.
    for (const stat of STAT_ORDER) {
      const delta = effect.statDeltas[stat];
      if (delta === undefined || !Number.isFinite(delta)) continue;
      statDeltas[stat] = (statDeltas[stat] ?? 0) + delta;
    }
  }

  return { statDeltas, armor: { threshold, rating }, damageModifiers, effects: EMPTY_EFFECTS };
}

/**
 * Two loadouts as one, and the only place gear and perks meet.
 *
 * Stats and armour add, levers multiply, modifiers concatenate with the left operand first. Order
 * is fixed rather than commutative-by-luck: `DamageModifier` applies `flat` before `scale`, so the
 * sequence the list arrives in is observable and the caller passing gear first is part of the
 * contract. `EMPTY_LOADOUT` is the identity of the whole thing.
 */
export function combineLoadouts(gear: Loadout, perks: Loadout): Loadout {
  const statDeltas: StatDeltas = {};

  for (const stat of STAT_ORDER) {
    const sum = (gear.statDeltas[stat] ?? 0) + (perks.statDeltas[stat] ?? 0);

    if (sum !== 0) statDeltas[stat] = sum;
  }

  return {
    statDeltas,
    armor: {
      threshold: gear.armor.threshold + perks.armor.threshold,
      rating: gear.armor.rating + perks.armor.rating,
    },
    damageModifiers: [...gear.damageModifiers, ...perks.damageModifiers],
    effects: combineEffects(gear.effects, perks.effects),
  };
}

/**
 * The character's stats as the pipeline should see them. `statsFrom` clamps, so a stack of helmets
 * can never push Perception past the usable range or below it.
 */
export function statsWithLoadout(base: StatBlock, loadout: Loadout): StatBlock {
  return statsFrom({
    strength: base.strength + (loadout.statDeltas.strength ?? 0),
    perception: base.perception + (loadout.statDeltas.perception ?? 0),
    endurance: base.endurance + (loadout.statDeltas.endurance ?? 0),
    agility: base.agility + (loadout.statDeltas.agility ?? 0),
  });
}
